/**
 * Documentos de identidad por país (Paraguay, 10/2026). El catálogo viene de
 * GET /c/{id}/document-types: { defaultCountry, countries: [{ code, name,
 * documentTypes: [{ idType, label, pattern, example }] }] }.
 *
 * El formato es solo un AVISO (no bloquea): el staff carga documentos de
 * prueba y hay documentos reales poco comunes; la validación dura es del back.
 */

export const DEFAULT_DOCUMENT_COUNTRY = 'AR';

// Si el catálogo no se puede leer, el alta sigue funcionando como antes
// (Argentina + los tipos de siempre, sin formato).
export const FALLBACK_DOCUMENT_CATALOG = {
  defaultCountry: null,
  countries: [
    {
      code: 'AR',
      name: 'Argentina',
      documentTypes: [
        { idType: 'DNI', label: 'DNI', pattern: null, example: '12345678' },
        {
          idType: 'PASSPORT',
          label: 'Pasaporte',
          pattern: null,
          example: 'AB123456'
        },
        { idType: 'CPF', label: 'CPF', pattern: null, example: '12345678909' },
        { idType: 'RUT', label: 'RUT', pattern: null, example: '12345678K' },
        {
          idType: 'CEDULA',
          label: 'Cédula',
          pattern: null,
          example: '1234567'
        }
      ]
    }
  ]
};

// Países que no están en el catálogo del back, para turistas de alquileres
// temporarios. Lista corta a propósito (la ISO completa ocupaba toda la
// pantalla): los de la tabla de nacionalidades + los que pidió la operación
// (KZ). El back acepta cualquier ISO alpha-2 con los tipos genéricos
// (DocumentCatalog.GENERIC); acá van con el pasaporte primero.
const OTHER_COUNTRY_CODES = [
  'DE',
  'AU',
  'AT',
  'BE',
  'BY',
  'CA',
  'CN',
  'KR',
  'CR',
  'HR',
  'CU',
  'SV',
  'ES',
  'US',
  'FR',
  'GT',
  'HN',
  'IN',
  'IE',
  'IL',
  'IT',
  'JP',
  'KZ',
  'MX',
  'NL',
  'PL',
  'PT',
  'GB',
  'DO',
  'RU',
  'CH',
  'UA',
  'VE'
];

export const GENERIC_DOCUMENT_TYPES = [
  {
    idType: 'PASSPORT',
    label: 'Pasaporte',
    pattern: '^[A-Z0-9]{5,15}$',
    example: 'AB123456'
  },
  {
    idType: 'CEDULA',
    label: 'Documento nacional',
    pattern: '^[A-Z0-9]{4,15}$',
    example: 'A1234567'
  }
];

const countryName = code => {
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' }).of(code) || code;
  } catch (e) {
    return code;
  }
};

/**
 * Suma al catálogo los países de OTHER_COUNTRY_CODES (marcados `other`), ordenados
 * por nombre, con los tipos genéricos. Los del catálogo quedan primero.
 */
export const withOtherCountries = catalog => {
  const countries = getDocumentCountries(catalog);
  const known = new Set(countries.map(country => country.code));
  const others = OTHER_COUNTRY_CODES.filter(code => !known.has(code))
    .map(code => ({
      code,
      name: countryName(code),
      documentTypes: GENERIC_DOCUMENT_TYPES,
      other: true
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
  return { ...catalog, countries: [...countries, ...others] };
};

// Puntos, guiones y espacios no son parte del número; mayúsculas para los
// documentos alfanuméricos (pasaporte, RUT con K).
export const normalizeDocumentNumber = value =>
  value == null
    ? value
    : String(value)
        .toUpperCase()
        .replace(/[.\-\s]/g, '');

export const getDocumentCountries = catalog =>
  (catalog && catalog.countries) || [];

export const findDocumentCountry = (catalog, code) =>
  getDocumentCountries(catalog).find(country => country.code === code) || null;

export const getDocumentTypes = (catalog, code) => {
  const country = findDocumentCountry(catalog, code);
  return country ? country.documentTypes || [] : [];
};

export const findDocumentType = (catalog, code, idType) =>
  getDocumentTypes(catalog, code).find(type => type.idType === idType) || null;

// País a preseleccionar: el de la comunidad; si no tiene, Argentina; si
// tampoco está en el catálogo, el primero que haya.
export const resolveDefaultDocumentCountry = catalog => {
  const countries = getDocumentCountries(catalog);
  const preferred = [
    catalog && catalog.defaultCountry,
    DEFAULT_DOCUMENT_COUNTRY
  ].find(code => code && countries.some(country => country.code === code));
  if (preferred) {
    return preferred;
  }
  return countries.length ? countries[0].code : DEFAULT_DOCUMENT_COUNTRY;
};

// Same deduction as the back (DocumentCountry): a person without country but
// with one of these types is from that country, whatever the community is.
const COUNTRY_BY_TYPE = { DNI: 'AR', CPF: 'BR', RUT: 'CL' };

/**
 * Country to show for a person: the stored one; if empty, the one implied by
 * the document type; if none, the community's default.
 */
export const resolveDocumentCountry = (catalog, idCountry, idType) => {
  if (idCountry && findDocumentCountry(catalog, idCountry)) {
    return idCountry;
  }
  const implied = COUNTRY_BY_TYPE[idType];
  if (implied && findDocumentCountry(catalog, implied)) {
    return implied;
  }
  return resolveDefaultDocumentCountry(catalog);
};

const describePattern = pattern => {
  const digits = /^\^\\d\{(\d+)(?:,(\d+))?\}\$$/.exec(pattern || '');
  if (digits) {
    return digits[2]
      ? `entre ${digits[1]} y ${digits[2]} dígitos`
      : `${digits[1]} dígitos`;
  }
  return null;
};

/**
 * Aviso (no error) si el número no tiene el formato esperado para el país y
 * tipo elegidos. undefined si está bien o si no hay formato para comparar.
 */
export const documentFormatWarning = (catalog, code, idType, number) => {
  const normalized = normalizeDocumentNumber(number);
  if (!normalized) {
    return undefined;
  }
  const type = findDocumentType(catalog, code, idType);
  if (!type || !type.pattern) {
    return undefined;
  }
  let valid;
  try {
    valid = new RegExp(type.pattern).test(normalized);
  } catch (e) {
    return undefined;
  }
  if (valid) {
    return undefined;
  }
  const country = findDocumentCountry(catalog, code);
  const expected = describePattern(type.pattern);
  const suffix = expected
    ? `suele tener ${expected}`
    : `suele tener un formato como ${type.example}`;
  return `${type.label}${
    country ? ` de ${country.name}` : ''
  }: ${suffix}. Revisalo antes de continuar.`;
};

// Texto corto para mostrar un documento: "Cédula PY 1234567".
export const formatDocument = (catalog, idCountry, idType, number) => {
  const type = findDocumentType(catalog, idCountry, idType);
  const label = type ? type.label : idType;
  return [label, idCountry, number].filter(Boolean).join(' ');
};

// Tipo de documento para una lista: "Documento nacional (ES)", "DNI (AR)".
// Sin país guardado, el nombre del tipo según el país que se deduce, sin sigla.
export const documentTypeLabel = (catalog, idCountry, idType) => {
  if (!idType) {
    return '';
  }
  const country = resolveDocumentCountry(catalog, idCountry, idType);
  const type = findDocumentType(catalog, country, idType);
  const label = type ? type.label : idType;
  return idCountry ? `${label} (${idCountry})` : label;
};

// El 409 de "documento de otra persona" (personal.id.conflict) del back.
export const PERSONAL_ID_CONFLICT_MESSAGE =
  'Ese número de documento ya está registrado para otra persona. Revisá que esté bien escrito.';

export const isPersonalIdConflict = apiErrors =>
  Array.isArray(apiErrors) &&
  apiErrors.some(
    error =>
      error &&
      (error.code === 'personal.id.conflict' ||
        String(error.detail || error.message || '').includes(
          'personal.id.conflict'
        ))
  );
